import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MessageHandlerService } from './message-handler.service';
import { LoginData } from '../login/login-data';

@Injectable({
    providedIn: 'root'
})
export class ApiRequestService {
    /** Értesít, amikor a töltésjelzőt meg kell szakítani. */
    apiCallEnded$: Observable<void>;

    /** Értesít, amikor a töltésjelzőt el kell indítani. */
    apiCallStarted$: Observable<void>;

    private _apiCallEnded: Subject<void> = new Subject<void>();

    private _apiCallStarted: Subject<void> = new Subject<void>();

    constructor(private _messageHandlerService: MessageHandlerService, private _http: HttpClient) {
        this.apiCallEnded$ = this._apiCallEnded.asObservable();
        this.apiCallStarted$ = this._apiCallStarted.asObservable();
    }
    /**
     * Fájl letöltése.
     * @param apiUrl Útvonal
     * @param urlParams URL paraméterek
     * @returns
     */
    downloadJsonRequest(url: string): Observable<unknown> {
        return this._http
            .get(url, {
                responseType: 'json',
                observe: 'response'
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    return throwError(() => error);
                })
            )
            .pipe(
                map((result: HttpResponse<unknown>) => {
                    return result.body;
                })
            );
    }

    /**
     * Általános 'POST' API hívást indít a szerver felé, a paraméterben kapott T típusú kéréssel.
     * U típusú választ ad vissza.
     * @param request Kérés
     * @param apiUrl Útvonal, például "api/QadCancelRequest"
     * @param triggerLoading Töltés jelző
     * @param errorHandled Van hibakezelés?
     * @param authenticationNeeded Szükséges-e az autentikáció?
     * @returns
     */

    sendRequest<T, U>(backendUrl: string, errorHandled = true, request: T, triggerLoading = true, apiUrl: string): Observable<U> {
        let headers = {};
        if (sessionStorage.getItem('token')) {
            const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

            headers = new HttpHeaders({ 'content-type': 'application/json', Authorization: 'Bearer ' + token.access_token });
        } else {
            headers = { 'content-type': 'application/json' };
        }

        if (triggerLoading) {
            this.handleApiCallStarted();
        }
        return this._http
            .post<U>(backendUrl + apiUrl, JSON.stringify(request), {
                headers,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }

    sendAuthorizedRequest<T, U>(backendUrl: string, errorHandled = true, request: T, triggerLoading = true, apiUrl: string): Observable<U> {
        const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

        let headers = new HttpHeaders({ 'content-type': 'application/json', Authorization: 'Bearer ' + token.access_token });

        if (triggerLoading) {
            this.handleApiCallStarted();
        }
        return this._http
            .post<U>(backendUrl + apiUrl, JSON.stringify(request), {
                headers,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }

    sendRequestBlob<T, U>(backendUrl: string, errorHandled = true, request: T, triggerLoading = true, apiUrl: string): Observable<U> {
        let headers = {};
        if (sessionStorage.getItem('token')) {
            const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

            headers = new HttpHeaders({ 'content-type': 'application/json', Authorization: 'Bearer ' + token.access_token });
        } else {
            headers = { 'content-type': 'application/json' };
        }
        if (triggerLoading) {
            this.handleApiCallStarted();
        }
        return this._http
            .post<U>(backendUrl + apiUrl, JSON.stringify(request), {
                headers,
                observe: 'response',
                responseType: 'blob' as 'json'
            })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }

    /**
     * Lehetővé teszi a szervertől érkező GET/POST API hívások válaszának feldolgozását, a válasz megérkeztének rögzítését.
     * @param response Szervertől érkező válasz
     * @param errorHandled Van hibakezelés?
     * @param triggerLoading Töltés jelző
     * @returns
     */
    private _handleApiResponse<U>(response: HttpResponse<U>, errorHandled: boolean, triggerLoading: boolean): U {
        if (triggerLoading) {
            this.handleApiCallEnded();
        }
        return response.body as U;
    }

    /**
     * Lehetővé teszi töltőképernyő/töltési státusz kijelzését a hívás idejére - az API hívás végének állapotjelzésével.
     */
    handleApiCallEnded(): void {
        this._apiCallEnded.next();
    }

    /**
     * Lehetővé teszi töltőképernyő/töltési státusz kijelzését a hívás idejére - az API hívás kezdetének állapotjelzésével.
     */
    handleApiCallStarted(): void {
        this._apiCallStarted.next();
    }

    //uploadFile() and updateFile are the same methods, it is just more readable if i separate these
    uploadFile<T, U>(file: File, errorHandled = true, triggerLoading = true, baseUrl: string, apiUrl: string): Observable<U> {
        let headers = {};

        if (sessionStorage.getItem('token')) {
            const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

            headers = new HttpHeaders({ Authorization: 'Bearer ' + token.access_token });
        }

        const formData = new FormData();

        formData.append('file', file, file.name);

        return this._http
            .post<U>(baseUrl + apiUrl, formData, {
                headers,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }

    uploadFileToASpecificFolder<T, U>(file: File, folderName:string, errorHandled = true, triggerLoading = true, baseUrl: string, apiUrl: string): Observable<U> {
        let headers = {};

        if (sessionStorage.getItem('token')) {
            const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

            headers = new HttpHeaders({ Authorization: 'Bearer ' + token.access_token });
        }

        // const formData = new FormData();

        // formData.append('file', file, file.name);

        if (triggerLoading) {
            this.handleApiCallStarted();
        }
        const formData = new FormData();
        formData.append('file', file, folderName+'-'+file.name);

        return this._http
            .post<U>(baseUrl + apiUrl, formData, {
                headers,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }

    updateFile<T, U>(
        file: File,
        folderName:string,
        errorHandled = true,
        triggerLoading = true,
        baseUrl: string,
        apiUrl: string
    ): Observable<U> {

        let headers = {};


        if (sessionStorage.getItem('token')) {
            const token = JSON.parse(sessionStorage.getItem('token') as string) as LoginData;

            headers = new HttpHeaders({ Authorization: 'Bearer ' + token.access_token });

        }

        if (triggerLoading) {
            this.handleApiCallStarted();
        }
        const formData = new FormData();
        formData.append('file', file, folderName+'-'+file.name);

        return this._http
        .post<U>(baseUrl + apiUrl, formData, {
            headers,
            observe: 'response'

        })
            .pipe(
                map((response: any) => {
                    return this._handleApiResponse(response, errorHandled, triggerLoading);
                })
            );
    }
}
